import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { formatDate } from './utils';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_caredata-suite/artifacts/8itk90ub_55315630-dcf1-43bd-9c0d-4f1818f5e217.jpeg";
const CLINIC_NAME = "Apex Medical Group";

export const generatePatientSummaryPDF = (summaryData) => {
  const { patient, recent_encounters, clinical_notes, generated_at, generated_by } = summaryData;
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  
  // Header
  doc.setFillColor(15, 23, 42);
  doc.rect(0, 0, pageWidth, 40, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text(CLINIC_NAME, 20, 25);
  
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text('PATIENT SUMMARY', pageWidth - 20, 20, { align: 'right' });
  doc.text(patient.patient_id, pageWidth - 20, 28, { align: 'right' });
  
  // Patient Info
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(`${patient.first_name} ${patient.last_name}`, 20, 55);
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  
  const patientInfo = [
    ['Date of Birth', formatDate(patient.date_of_birth)],
    ['Gender', patient.gender],
    ['Blood Type', patient.blood_type || 'Unknown'],
    ['Phone', patient.phone || 'N/A'],
    ['Email', patient.email || 'N/A'],
    ['Address', patient.address || 'N/A'],
  ];
  
  let yPos = 65;
  patientInfo.forEach(([label, value]) => {
    doc.setFont('helvetica', 'bold');
    doc.text(`${label}:`, 20, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(value, 60, yPos);
    yPos += 6;
  });
  
  // Allergies
  yPos += 5;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('Allergies', 20, yPos);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  yPos += 6;
  
  if (patient.allergies && patient.allergies.length > 0) {
    doc.setTextColor(220, 38, 38);
    doc.text(patient.allergies.join(', '), 20, yPos);
    doc.setTextColor(0, 0, 0);
  } else {
    doc.text('No known allergies', 20, yPos);
  }
  
  // Active Problems
  yPos += 10;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('Active Problems', 20, yPos);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  yPos += 6;
  
  if (patient.active_problems && patient.active_problems.length > 0) {
    patient.active_problems.forEach((problem) => {
      doc.text(`• ${problem}`, 25, yPos);
      yPos += 5;
    });
  } else {
    doc.text('None documented', 20, yPos);
    yPos += 5;
  }
  
  // Medications
  yPos += 5;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('Current Medications', 20, yPos);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  yPos += 6;
  
  if (patient.medications && patient.medications.length > 0) {
    patient.medications.forEach((med) => {
      doc.text(`• ${med}`, 25, yPos);
      yPos += 5;
    });
  } else {
    doc.text('None documented', 20, yPos);
    yPos += 5;
  }
  
  // Recent Encounters
  if (recent_encounters && recent_encounters.length > 0) {
    yPos += 10;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Recent Encounters', 20, yPos);
    yPos += 5;
    
    const encounterData = recent_encounters.slice(0, 5).map(enc => [
      formatDate(enc.created_at),
      enc.form_name,
      enc.submitted_by_name,
    ]);
    
    doc.autoTable({
      startY: yPos,
      head: [['Date', 'Type', 'Provider']],
      body: encounterData,
      theme: 'striped',
      headStyles: { fillColor: [15, 23, 42] },
      styles: { fontSize: 9 },
      margin: { left: 20, right: 20 },
    });
    
    yPos = doc.lastAutoTable.finalY + 10;
  }
  
  // Clinical Notes Summary
  if (clinical_notes && clinical_notes.length > 0) {
    // Check if we need a new page
    if (yPos > 220) {
      doc.addPage();
      yPos = 20;
    }
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Recent Clinical Notes', 20, yPos);
    yPos += 6;
    
    clinical_notes.slice(0, 3).forEach((note) => {
      if (yPos > 260) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text(`${note.note_type.toUpperCase()} - ${formatDate(note.created_at)}`, 20, yPos);
      doc.setFont('helvetica', 'normal');
      yPos += 4;
      
      const noteText = note.final_note || note.ai_generated_note;
      const lines = doc.splitTextToSize(noteText.substring(0, 500), pageWidth - 40);
      doc.text(lines, 20, yPos);
      yPos += lines.length * 4 + 8;
    });
  }
  
  // Footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(`Generated by ${generated_by} on ${formatDate(generated_at)}`, 20, 285);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - 20, 285, { align: 'right' });
    doc.text('CONFIDENTIAL - Protected Health Information', pageWidth / 2, 290, { align: 'center' });
  }
  
  // Save
  doc.save(`Patient_Summary_${patient.patient_id}_${new Date().toISOString().split('T')[0]}.pdf`);
};

export const generateInvoicePDF = (invoice) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  
  // Header
  doc.setFillColor(15, 23, 42);
  doc.rect(0, 0, pageWidth, 40, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text(CLINIC_NAME, 20, 25);
  
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text('INVOICE', pageWidth - 20, 20, { align: 'right' });
  doc.text(invoice.invoice_number, pageWidth - 20, 28, { align: 'right' });
  
  // Reset
  doc.setTextColor(0, 0, 0);
  
  // Bill To
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Bill To:', 20, 55);
  doc.setFont('helvetica', 'normal');
  doc.text(invoice.patient_name, 20, 62);
  if (invoice.patient_address) {
    doc.text(invoice.patient_address, 20, 69);
  }
  
  // Invoice details
  doc.setFont('helvetica', 'bold');
  doc.text('Invoice Date:', pageWidth - 70, 55);
  doc.text('Due Date:', pageWidth - 70, 62);
  doc.text('Status:', pageWidth - 70, 69);
  
  doc.setFont('helvetica', 'normal');
  doc.text(formatDate(invoice.created_at), pageWidth - 20, 55, { align: 'right' });
  doc.text(invoice.due_date ? formatDate(invoice.due_date) : 'Upon Receipt', pageWidth - 20, 62, { align: 'right' });
  doc.text(invoice.status.toUpperCase(), pageWidth - 20, 69, { align: 'right' });
  
  // Line items table
  const tableData = invoice.line_items.map(item => [
    item.description,
    item.quantity.toString(),
    `$${item.unit_price.toFixed(2)}`,
    item.adjustment > 0 ? `-$${item.adjustment.toFixed(2)}` : '-',
    `$${item.total.toFixed(2)}`
  ]);
  
  doc.autoTable({
    startY: 85,
    head: [['Description', 'Qty', 'Unit Price', 'Adjustment', 'Total']],
    body: tableData,
    theme: 'striped',
    headStyles: { fillColor: [15, 23, 42] },
    styles: { fontSize: 9 },
    columnStyles: {
      0: { cellWidth: 80 },
      1: { cellWidth: 20, halign: 'center' },
      2: { cellWidth: 30, halign: 'right' },
      3: { cellWidth: 30, halign: 'right' },
      4: { cellWidth: 30, halign: 'right' },
    },
  });
  
  // Totals
  const finalY = doc.lastAutoTable.finalY + 10;
  
  doc.setFont('helvetica', 'normal');
  doc.text('Subtotal:', pageWidth - 70, finalY);
  doc.text(`$${invoice.subtotal.toFixed(2)}`, pageWidth - 20, finalY, { align: 'right' });
  
  doc.text('Adjustments:', pageWidth - 70, finalY + 7);
  doc.text(`-$${invoice.total_adjustments.toFixed(2)}`, pageWidth - 20, finalY + 7, { align: 'right' });
  
  doc.setLineWidth(0.5);
  doc.line(pageWidth - 70, finalY + 12, pageWidth - 20, finalY + 12);
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('Total Due:', pageWidth - 70, finalY + 20);
  doc.text(`$${invoice.total.toFixed(2)}`, pageWidth - 20, finalY + 20, { align: 'right' });
  
  // Notes
  if (invoice.notes) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Notes:', 20, finalY + 35);
    doc.setFont('helvetica', 'normal');
    doc.text(invoice.notes, 20, finalY + 42, { maxWidth: pageWidth - 40 });
  }
  
  // Footer
  doc.setFontSize(8);
  doc.setTextColor(128, 128, 128);
  doc.text('Thank you for choosing ' + CLINIC_NAME, pageWidth / 2, 280, { align: 'center' });
  doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 285, { align: 'center' });
  
  // Save
  doc.save(`Invoice_${invoice.invoice_number}.pdf`);
};
